import { zodResolver } from '@hookform/resolvers/zod';
import { Box } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InputField } from 'src/components/atoms/input-field';
import { Dialog, DialogActions } from 'src/components/molecules/dialog';

import { useCreateHostValidationSchema } from './schema/create-host-dialog-validation-schema';

import { useCreateHost } from '@/features/hosts/hooks/mutation/use-create-host';

interface FormValues {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

const defaultValues = {
  email: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
};

const CreateHostDialog = ({ open, onCancel }: DialogActions) => {
  const { t } = useTranslation(['hosts', 'common']);

  const validationSchema = useCreateHostValidationSchema();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<FormValues>({
    defaultValues,
    mode: 'onBlur',
    resolver: zodResolver(validationSchema),
  });

  const { mutate, isPending } = useCreateHost(onCancel);

  const onSubmit = async (data: FormValues) => {
    mutate({ ...data, phoneNumber: data.phoneNumber?.toString() });
  };

  useEffect(() => {
    if (!open) {
      reset(defaultValues);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onCancel={onCancel}
      variant="info"
      title={t('hosts:dialogs.invite.title')}
      isConfirmButtonSubmitting={isPending}
      confirmButtonText={t('common:button.invite')}
      isConfirmButtonDisabled={!isValid}
      onConfirm={handleSubmit(onSubmit)}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
        <Controller
          control={control}
          name="email"
          render={({ fieldState: { error }, field: { value, name, onBlur, onChange } }) => (
            <InputField
              value={value}
              name={name}
              label={t('hosts:dialogs.invite.email')}
              fullWidth
              required
              onBlur={onBlur}
              onChange={onChange}
              error={!!error?.message}
              helperText={error?.message}
            />
          )}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Controller
            control={control}
            name="firstName"
            render={({ fieldState: { error }, field: { value, name, onBlur, onChange } }) => (
              <InputField
                value={value}
                name={name}
                label={t('hosts:dialogs.invite.firstName')}
                fullWidth
                onBlur={onBlur}
                onChange={onChange}
                error={!!error?.message}
                helperText={error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="lastName"
            render={({ fieldState: { error }, field: { value, name, onBlur, onChange } }) => (
              <InputField
                value={value}
                name={name}
                label={t('hosts:dialogs.invite.lastName')}
                fullWidth
                onBlur={onBlur}
                onChange={onChange}
                error={!!error?.message}
                helperText={error?.message}
              />
            )}
          />
        </Box>
        <Controller
          control={control}
          name="phoneNumber"
          render={({ fieldState: { error }, field: { value, name, onBlur, onChange } }) => (
            <InputField
              value={value}
              name={name}
              label={t('hosts:dialogs.invite.phoneNumber')}
              fullWidth
              type="number"
              onBlur={onBlur}
              onChange={onChange}
              error={!!error?.message}
              helperText={error?.message}
            />
          )}
        />
      </Box>
    </Dialog>
  );
};

export default CreateHostDialog;
